import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Users, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal shown when a user taps a workgroup-invite notification.
 * Accept → join the workgroup. Decline → mark the invite rejected.
 */
export default function InviteResponseModal({ invite, onClose, onAccept, onDecline }) {
    const [busy, setBusy] = useState(null); // "accept" | "decline" | null

    if (!invite) return null;

    const handle = async (action) => {
        setBusy(action);
        try {
            if (action === "accept") await onAccept(invite.id);
            else await onDecline(invite.id);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Couldn't update invite — please retry",
            );
        } finally {
            setBusy(null);
        }
    };

    return (
        <Dialog open={!!invite} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent
                className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-2rem)] max-w-[440px] p-0 overflow-hidden"
                data-testid="invite-response-modal"
            >
                <div className="px-6 py-5 bg-gradient-to-b from-[#00A3FF]/15 to-transparent border-b border-[#2A3655]">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#00A3FF]/20 border border-[#00A3FF]/60 flex items-center justify-center">
                            <Users className="w-6 h-6 text-[#00A3FF]" />
                        </div>
                        <div>
                            <DialogHeader className="space-y-0">
                                <DialogTitle className="text-white text-base">
                                    Workgroup invitation
                                </DialogTitle>
                                <DialogDescription className="text-slate-400 text-xs">
                                    Join a shared team workspace
                                </DialogDescription>
                            </DialogHeader>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-5 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                        You've been invited to
                    </div>
                    <div
                        data-testid="invite-workgroup-name"
                        className="text-xl font-bold text-white truncate"
                    >
                        {invite.workgroup_name || "a workgroup"}
                    </div>
                    {invite.invited_by_name && (
                        <div className="text-sm text-slate-300">
                            by <span className="text-white font-semibold">{invite.invited_by_name}</span>
                        </div>
                    )}
                    <p className="text-[11px] text-slate-400 leading-relaxed pt-2">
                        Accepting will let you read and create shared templates, contacts, leads, and projects based on the workgroup's permissions.
                    </p>
                </div>

                <div className="flex items-center gap-2 px-6 py-4 border-t border-[#2A3655] bg-[#0A1128]">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handle("decline")}
                        disabled={!!busy}
                        data-testid="invite-decline-button"
                        className="flex-1 text-slate-300 hover:text-white hover:bg-rose-500/10 border border-[#2A3655]"
                    >
                        {busy === "decline" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>
                                <X className="w-4 h-4 mr-1.5" />
                                Decline
                            </>
                        )}
                    </Button>
                    <Button
                        type="button"
                        onClick={() => handle("accept")}
                        disabled={!!busy}
                        data-testid="invite-accept-button"
                        className="flex-1 bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold"
                    >
                        {busy === "accept" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>
                                <Check className="w-4 h-4 mr-1.5" />
                                Accept
                            </>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
